import { virtualModelAPI } from './api';

/** 前端展示用：id 系统模特为 number，我的模特为 string(uuid) */
export interface VirtualModelItem {
  id: number | string;
  name: string;
  image: string;
  /** 我的模特：上传视频可播放地址 */
  video?: string | null;
  /** 仅我的模特：来源标识，便于区分 */
  isMine?: boolean;
}

/** 系统内置模特（静态列表） */
const SYSTEM_MODELS: VirtualModelItem[] = [
  { id: 1, name: '朱玉婷', image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20businesswoman%20in%20elegant%20suit%20smiling%20confidently%2C%20corporate%20headshot%20portrait%2C%20clean%20white%20background%2C%20modern%20professional%20style%2C%20warm%20lighting&width=160&height=200&seq=model1&orientation=portrait' },
  { id: 2, name: '郑俊辉', image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20businessman%20in%20navy%20suit%20and%20tie%2C%20confident%20corporate%20portrait%2C%20clean%20white%20background%2C%20executive%20headshot%2C%20professional%20lighting&width=160&height=200&seq=model2&orientation=portrait' },
  { id: 3, name: 'party', image: 'https://readdy.ai/api/search-image?query=Stylish%20young%20woman%20with%20colorful%20party%20lights%20background%2C%20festive%20celebration%20portrait%2C%20vibrant%20neon%20colors%2C%20energetic%20atmosphere&width=160&height=200&seq=model3&orientation=portrait' },
  { id: 4, name: 'hessianlyn', image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20man%20in%20casual%20business%20attire%20smiling%2C%20modern%20corporate%20portrait%2C%20neutral%20background%2C%20friendly%20approachable%20style&width=160&height=200&seq=model4&orientation=portrait' },
  { id: 5, name: '钱芸', image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20businesswoman%20in%20white%20blouse%2C%20elegant%20corporate%20portrait%2C%20clean%20background%2C%20sophisticated%20professional%20style&width=160&height=200&seq=model5&orientation=portrait' },
  { id: 6, name: '叶丰瑞', image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20woman%20in%20business%20casual%20attire%2C%20modern%20corporate%20headshot%2C%20neutral%20background%2C%20confident%20professional%20style&width=160&height=200&seq=model6&orientation=portrait' },
  { id: 7, name: '吴昕悦', image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20woman%20in%20elegant%20business%20attire%20with%20city%20skyline%20background%2C%20modern%20corporate%20portrait%2C%20evening%20urban%20setting&width=160&height=200&seq=model7&orientation=portrait' },
  { id: 8, name: '李颖娜', image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20businessman%20in%20dark%20suit%2C%20confident%20executive%20portrait%2C%20modern%20office%20background%2C%20professional%20corporate%20style&width=160&height=200&seq=model8&orientation=portrait' },
  { id: 9, name: '李倩', image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20woman%20in%20casual%20business%20attire%20outdoors%2C%20natural%20lighting%20portrait%2C%20soft%20background%2C%20approachable%20professional%20style&width=160&height=200&seq=model9&orientation=portrait' },
  { id: 10, name: '周雨婷', image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20businesswoman%20in%20light%20blue%20blouse%2C%20clean%20corporate%20portrait%2C%20white%20background%2C%20friendly%20professional%20style&width=160&height=200&seq=model10&orientation=portrait' },
  { id: 11, name: '周庆', image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20woman%20in%20elegant%20black%20business%20attire%2C%20sophisticated%20corporate%20portrait%2C%20neutral%20background%2C%20confident%20executive%20style&width=160&height=200&seq=model11&orientation=portrait' },
  { id: 12, name: '欧阳昭', image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20businessman%20in%20formal%20suit%2C%20confident%20corporate%20portrait%2C%20clean%20background%2C%20executive%20professional%20style&width=160&height=200&seq=model12&orientation=portrait' },
];

/** 获取系统模特列表 */
export function getSystemModels(): VirtualModelItem[] {
  return SYSTEM_MODELS;
}

/** 数据库中的虚拟模特记录 */
export interface VirtualModelRecord {
  id: string;
  user_id: string;
  name: string;
  image_url: string | null;
  video_url?: string | null;
  created_at: string;
  updated_at?: string;
}

/** 获取当前用户的「我的模特」列表 */
export async function getMyModels(userId: string): Promise<VirtualModelItem[]> {
  try {
    const data = await virtualModelAPI.getUserVirtualModels(userId);
    
    return data.map((row: any) => ({
      id: row.id,
      name: row.name,
      image: row.image_url || 'https://readdy.ai/api/search-image?query=Professional%20portrait%20placeholder&width=160&height=200&orientation=portrait',
      video: row.video_url || null,
      isMine: true,
    }));
  } catch (e) {
    console.error('获取我的模特列表失败', e);
    return [];
  }
}

/** 创建虚拟模特（用户上传后写入库，含视频文件） */
export async function createVirtualModel(
  userId: string,
  payload: { name: string; image_url?: string | null; video_base64?: string | null }
): Promise<{ success: boolean; id?: string; error?: string }> {
  try {
    const response = await virtualModelAPI.createVirtualModel({
      user_id: userId,
      name: payload.name,
      image_url: payload.image_url,
      video_base64: payload.video_base64,
    });
    return { success: true, id: response.id };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : '创建虚拟模特失败';
    return { success: false, error: message };
  }
}
